import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { configService } from '../services/configService';
import { roleAccessService } from '../services/roleAccessService';

const CONFIG_DEFAULTS = {
  vat_rate: '15',
  fy_start_month: '1',
  company_logo: '',
};

/**
 * AppContext — global state for the active user's selected role, site, and
 * budget version, plus the full list of role+site assignments available to them.
 *
 * Why mirror to localStorage:
 *   The axios interceptor in src/services/api.js can't read React context
 *   directly (it lives outside the React tree). So every setter here also
 *   writes to localStorage. The interceptor reads from localStorage on each
 *   request. This means switching role/site instantly affects the next
 *   network call with no extra wiring.
 *
 * Initialisation:
 *   The state is seeded from localStorage on first render so a page reload
 *   restores the user's last context without a flash of empty values.
 *
 * Population:
 *   After login, authService stores the assignments list and the default
 *   assignment in localStorage. AppProvider picks those up via the same
 *   seeding pattern. If you need to refresh assignments mid-session (e.g.
 *   an admin granted you a new role), call setAssignments() with the new list.
 */

const AppContext = createContext(null);

export const useAppContext = () => {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error('useAppContext must be used inside <AppProvider>');
  }
  return ctx;
};

const readJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const AppProvider = ({ children }) => {
  // Seed from localStorage so a refresh doesn't lose state
  const [selectedRole, setSelectedRoleState] = useState(
    () => localStorage.getItem('roleUrid') || ''
  );
  const [selectedSite, setSelectedSiteState] = useState(
    () => localStorage.getItem('siteCode') || ''
  );
  const [selectedVersion, setSelectedVersionState] = useState(
    () => localStorage.getItem('budgetVersionId') || ''
  );
  const [assignments, setAssignmentsState] = useState(
    () => readJSON('assignments', [])
  );

  // Tenant config (VAT rate, FY start month, …). Seeded from localStorage so a
  // reload doesn't flash defaults, then refreshed from the server on mount.
  const [config, setConfigState] = useState(() => readJSON('appConfig', CONFIG_DEFAULTS));

  const refreshConfig = useCallback(async () => {
    try {
      const res = await configService.getConfig();
      if (res?.success) {
        const merged = { ...CONFIG_DEFAULTS, ...res.data };
        setConfigState(merged);
        localStorage.setItem('appConfig', JSON.stringify(merged));
      }
    } catch {
      /* keep last-known config */
    }
  }, []);

  useEffect(() => { refreshConfig(); }, [refreshConfig]);

  // Convenience typed accessors.
  const fyStartMonth = Math.min(12, Math.max(1, parseInt(config.fy_start_month, 10) || 1));
  const vatRate = Number(config.vat_rate) || 0;

  // Module access for the CURRENTLY-SELECTED role — drives sidebar visibility.
  const [roleModules, setRoleModules] = useState(() => readJSON('roleModules', []));
  useEffect(() => {
    let cancelled = false;
    if (!selectedRole) { setRoleModules([]); localStorage.removeItem('roleModules'); return; }
    roleAccessService.getRoleModules(selectedRole)
      .then((r) => {
        if (cancelled || !r?.success) return;
        const mods = r.modules || [];
        setRoleModules(mods);
        localStorage.setItem('roleModules', JSON.stringify(mods));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedRole]);

  // -------- setters that mirror to localStorage --------

  const setSelectedRole = (value) => {
    setSelectedRoleState(value);
    if (value === '' || value === null || value === undefined) {
      localStorage.removeItem('roleUrid');
    } else {
      localStorage.setItem('roleUrid', String(value));
    }
  };

  const setSelectedSite = (value) => {
    setSelectedSiteState(value);
    if (value === '' || value === null || value === undefined) {
      localStorage.removeItem('siteCode');
    } else {
      localStorage.setItem('siteCode', String(value));
    }
  };

  const setSelectedVersion = (value) => {
    setSelectedVersionState(value);
    if (value === '' || value === null || value === undefined) {
      localStorage.removeItem('budgetVersionId');
    } else {
      localStorage.setItem('budgetVersionId', String(value));
    }
  };

  const setAssignments = (list) => {
    setAssignmentsState(list || []);
    if (list && list.length) {
      localStorage.setItem('assignments', JSON.stringify(list));
    } else {
      localStorage.removeItem('assignments');
    }
  };

  // -------- cross-tab sync (optional, low cost) --------
  // If the user opens two tabs and changes role in one, the other tab
  // picks up the change on the next render. Without this, the two tabs
  // would silently drift apart.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'roleUrid') setSelectedRoleState(e.newValue || '');
      if (e.key === 'siteCode') setSelectedSiteState(e.newValue || '');
      if (e.key === 'budgetVersionId') setSelectedVersionState(e.newValue || '');
      if (e.key === 'assignments') {
        try {
          setAssignmentsState(e.newValue ? JSON.parse(e.newValue) : []);
        } catch {
          setAssignmentsState([]);
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = {
    selectedRole,
    setSelectedRole,
    selectedSite,
    setSelectedSite,
    selectedVersion,
    setSelectedVersion,
    assignments,
    setAssignments,
    config,
    refreshConfig,
    fyStartMonth,
    vatRate,
    roleModules,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};